import { getActiveDbName, onDbSwitch } from "./dbContext";
import { buildImportMergePlan } from "./importMerge";
import {
  type QueryRiskLevel,
  type RestParam,
  DEFAULT_QUERY_RISK_LEVEL,
  normalizeQueryRiskLevel,
  normalizeRemarks,
  normalizeRestParams,
  normalizeRestPathAndParams,
} from "@spuerhund/shared/queryMetadata";
import type {
  CatalogKind,
  QueryOperationConfig,
  RequestType,
  ServerConnectionConfig,
  ServerKind,
} from "@spuerhund/shared/targets";

// ── IndexedDB persistence layer for Spürhund ────────────────────────
// Browser-first. No server required.

const DB_VERSION = 8;
const REQUIRED_STORES = [
  "servers",
  "queries",
  "schemaCache",
  "history",
  "secretRegistry",
  "sharedConfigRefs",
  "vaultMeta",
  "vaultEntries",
  "vaultDeviceKeys",
] as const;

export type VaultEntryKind = "secret" | "config";
export type VaultScopeType = "global" | "server";

// ── Types ────────────────────────────────────────────────────────────

export interface StoredServer {
  id: string;
  name: string;
  serverKind?: ServerKind;
  connectionConfig?: ServerConnectionConfig;
  baseUrl: string;
  graphqlPath?: string | undefined;
  restPath?: string | undefined;
  authType: "none" | "bearer" | "apikey" | "custom";
  authConfig: string; // JSON
  isProduction?: boolean | undefined;
  timeoutMs?: number | undefined;
  sharedConfigId?: string | undefined;
  sharedRole?: "owner" | "editor" | undefined;
  sharedLastKnownVersion?: number | undefined;
  sharedCapability?: string | undefined;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type RestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
export type RestContentType = "json" | "xml" | "text" | "form-urlencoded";
export type QuerySystemPreset = "adhoc-gql" | "adhoc-rest" | "adhoc-mongo" | "adhoc-sql";
export type { QueryRiskLevel, RestParam };
export type { RequestType };

export interface StoredQuery {
  id: string;
  serverId: string;
  name: string;
  queryText: string;
  variables: string;
  headers: string;
  remarks?: string;
  riskLevel?: QueryRiskLevel;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  pinnedAt?: string | null;
  category?: string;
  systemPreset?: QuerySystemPreset;
  lastUsedAt?: string | null;
  requestType?: RequestType;
  operationConfig?: QueryOperationConfig;
  restMethod?: RestMethod;
  restPath?: string;
  restParams?: RestParam[];
  restContentType?: RestContentType;
}

export interface StoredSchemaCache {
  serverId: string;
  catalogKind?: CatalogKind;
  catalogJson?: string;
  introspectionJson: string;
  sdl?: string | undefined;
  fetchedAt: string;
}

export interface StoredHistoryEntry {
  id?: number; // autoIncrement
  queryId?: string;
  serverId: string;
  queryText: string;
  variables: string;
  headers: string;
  responseJson?: string;
  responseBody?: string;
  responseContentType?: string;
  statusCode?: number;
  durationMs?: number;
  errorMessage?: string;
  executedAt: string;
  requestType?: RequestType;
  operationConfig?: QueryOperationConfig;
  restMethod?: string;
  restPath?: string;
  restContentType?: RestContentType;
}

export interface StoredVaultMeta {
  id: "vault";
  version: 1;
  salt: string;
  iterations: number;
  verificationCiphertext: string;
  verificationIv: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoredVaultEntry {
  id: string;
  key: string;
  kind: VaultEntryKind;
  scopeType: VaultScopeType;
  scopeServerId?: string | undefined;
  description: string;
  ciphertext: string;
  iv: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoredVaultRememberedKey {
  id: "remembered";
  key: CryptoKey;
  createdAt: string;
  updatedAt: string;
}

export interface SpuerhundExport {
  version: 2;
  exportedAt: string;
  servers: StoredServer[];
  queries: StoredQuery[];
  schemaCache: StoredSchemaCache[];
  history: StoredHistoryEntry[];
}

export interface LegacySpuerhundExport {
  version: 1;
  exportedAt: string;
  servers: StoredServer[];
  queries: StoredQuery[];
  schemaCache: StoredSchemaCache[];
  history: StoredHistoryEntry[];
}

export interface StoredSecret {
  name: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoredSharedConfigRef {
  configId: string;
  linkedServerId: string;
  role: "owner" | "editor";
  displayName: string;
  ownerSecret?: string;
  editorInvite?: string;
  sharedKey: string;
  clientId: string;
  lastKnownVersion: number;
  lastSyncedFingerprint?: string;
  lastSyncedAt?: string;
  lastRemoteUpdateAt?: string;
  lastRemoteUpdatedByClientId?: string;
  lastRemoteUpdatedByDisplayName?: string;
  remoteChanged?: boolean;
  managedByWorkspace?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SecretExportData {
  version: 1;
  exportedAt: string;
  secrets: StoredSecret[];
}

export interface SpuerhundVaultExport {
  version: 1;
  exportedAt: string;
  meta: StoredVaultMeta;
  entries: StoredVaultEntry[];
}

// ── DB lifecycle ─────────────────────────────────────────────────────

let dbInstance: IDBDatabase | null = null;

function splitUrlIntoBaseAndPath(rawUrl: string): { baseUrl: string; path?: string } {
  const trimmed = rawUrl.trim();
  if (!trimmed) return { baseUrl: "" };

  try {
    const url = new URL(trimmed);
    const path = `${url.pathname}${url.search}${url.hash}`;
    return {
      baseUrl: url.origin,
      ...(path && path !== "/" ? { path } : {}),
    };
  } catch {
    return { baseUrl: trimmed };
  }
}

function derivePathFromAbsoluteUrl(rawUrl: unknown, fallbackBaseUrl: string): string | undefined {
  if (typeof rawUrl !== "string") return undefined;
  const trimmed = rawUrl.trim();
  if (!trimmed) return undefined;

  try {
    const url = new URL(trimmed);
    if (fallbackBaseUrl && url.origin !== fallbackBaseUrl) {
      return `${url.pathname}${url.search}${url.hash}` || "/";
    }
    const path = `${url.pathname}${url.search}${url.hash}`;
    return path && path !== "/" ? path : undefined;
  } catch {
    return trimmed;
  }
}

function normalizeLegacyServerRecord(record: Record<string, unknown>): Record<string, unknown> {
  const split = splitUrlIntoBaseAndPath(typeof record.endpoint === "string" ? record.endpoint : "");
  const baseUrl = typeof record.baseUrl === "string" && record.baseUrl.trim()
    ? record.baseUrl
    : split.baseUrl;
  const next: Record<string, unknown> = {
    ...record,
    baseUrl,
  };

  const graphqlPath = typeof record.graphqlPath === "string"
    ? record.graphqlPath
    : typeof record.graphqlUrl === "string"
      ? derivePathFromAbsoluteUrl(record.graphqlUrl, baseUrl)
      : split.path;
  const restPath = typeof record.restPath === "string"
    ? record.restPath
    : typeof record.restBaseUrl === "string"
      ? derivePathFromAbsoluteUrl(record.restBaseUrl, baseUrl)
      : undefined;

  if (graphqlPath) next.graphqlPath = graphqlPath;
  else delete next.graphqlPath;

  if (restPath) next.restPath = restPath;
  else delete next.restPath;

  delete next.endpoint;
  delete next.graphqlUrl;
  delete next.restBaseUrl;
  delete next.executionMode;

  return next;
}

function normalizeStoredQueryRecord(record: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...record };
  normalized.remarks = normalizeRemarks(record.remarks);
  normalized.riskLevel = normalizeQueryRiskLevel(record.riskLevel);

  const { restPath, restParams } = normalizeRestPathAndParams(
    typeof record.restPath === "string" ? record.restPath : typeof record.rest_path === "string" ? record.rest_path : undefined,
    record.restParams ?? record.rest_params,
  );

  normalized.restPath = restPath;
  normalized.restParams = restParams;
  return normalized;
}

export function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(getActiveDbName(), DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const transaction = request.transaction;
      const oldVersion = event.oldVersion;

      if (!db.objectStoreNames.contains("servers")) {
        db.createObjectStore("servers", { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains("queries")) {
        const queryStore = db.createObjectStore("queries", { keyPath: "id" });
        queryStore.createIndex("serverId", "serverId", { unique: false });
      }

      if (!db.objectStoreNames.contains("schemaCache")) {
        db.createObjectStore("schemaCache", { keyPath: "serverId" });
      }

      if (!db.objectStoreNames.contains("history")) {
        const historyStore = db.createObjectStore("history", {
          keyPath: "id",
          autoIncrement: true,
        });
        historyStore.createIndex("serverId", "serverId", { unique: false });
      }

      if (!db.objectStoreNames.contains("secretRegistry")) {
        db.createObjectStore("secretRegistry", { keyPath: "name" });
      }

      if (!db.objectStoreNames.contains("sharedConfigRefs")) {
        const refStore = db.createObjectStore("sharedConfigRefs", { keyPath: "configId" });
        refStore.createIndex("linkedServerId", "linkedServerId", { unique: false });
      }

      if (!db.objectStoreNames.contains("vaultMeta")) {
        db.createObjectStore("vaultMeta", { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains("vaultEntries")) {
        db.createObjectStore("vaultEntries", { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains("vaultDeviceKeys")) {
        db.createObjectStore("vaultDeviceKeys", { keyPath: "id" });
      }

      if (transaction && oldVersion < 6 && db.objectStoreNames.contains("servers")) {
        const serverStore = transaction.objectStore("servers");
        const cursorRequest = serverStore.openCursor();
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor) return;

          const record = cursor.value as Record<string, unknown>;
          const normalized = normalizeLegacyServerRecord(record);
          if (JSON.stringify(record) === JSON.stringify(normalized)) {
            cursor.continue();
            return;
          }

          cursor.update(normalized);
          cursor.continue();
        };
      }

      if (transaction && oldVersion < 7 && db.objectStoreNames.contains("history")) {
        const historyStore = transaction.objectStore("history");
        const cursorRequest = historyStore.openCursor();
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor) return;

          const record = cursor.value as Record<string, unknown>;
          const normalized = {
            ...record,
            headers: typeof record.headers === "string" ? record.headers : "{}",
          };

          if (JSON.stringify(record) !== JSON.stringify(normalized)) {
            cursor.update(normalized);
          }

          cursor.continue();
        };
      }

      if (transaction && oldVersion < 8 && db.objectStoreNames.contains("queries")) {
        const queryStore = transaction.objectStore("queries");
        const cursorRequest = queryStore.openCursor();
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor) return;

          const record = cursor.value as Record<string, unknown>;
          const normalized = normalizeStoredQueryRecord(record);
          if (JSON.stringify(record) !== JSON.stringify(normalized)) {
            cursor.update(normalized);
          }
          cursor.continue();
        };
      }
    };

    request.onblocked = () => {
      reject(new Error("Local data upgrade is blocked by another Spürhund tab. Close other Spürhund tabs and reload."));
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        if (dbInstance === db) {
          dbInstance = null;
        }
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getDb(): Promise<IDBDatabase> {
  if (dbInstance && dbInstance.name) {
    // Verify the connection is still alive and has the current schema.
    try {
      if (REQUIRED_STORES.every((storeName) => dbInstance!.objectStoreNames.contains(storeName))) {
        dbInstance.transaction("servers", "readonly");
        return dbInstance;
      }

      dbInstance.close();
      dbInstance = null;
    } catch {
      dbInstance = null;
    }
  }
  dbInstance = await openDb();
  return dbInstance;
}

export function closeDb(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

export async function reopenDb(): Promise<IDBDatabase> {
  closeDb();
  dbInstance = await openDb();
  return dbInstance;
}

// ── Helpers ──────────────────────────────────────────────────────────

function tx(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
): IDBObjectStore {
  return db.transaction(storeName, mode).objectStore(storeName);
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function txComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function uuid(): string {
  return crypto.randomUUID();
}

function now(): string {
  return new Date().toISOString();
}

// ── Servers CRUD ─────────────────────────────────────────────────────

export async function listServers(): Promise<StoredServer[]> {
  const db = await getDb();
  const servers = await req<StoredServer[]>(tx(db, "servers", "readonly").getAll());
  return servers.sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function createServer(input: {
  id?: string;
  name: string;
  serverKind?: ServerKind;
  connectionConfig?: ServerConnectionConfig;
  baseUrl: string;
  graphqlPath?: string;
  restPath?: string;
  authType: string;
  authConfig?: string;
  isProduction?: boolean;
  timeoutMs?: number;
}): Promise<StoredServer> {
  const db = await getDb();

  // Determine next sortOrder
  const existing = await req<StoredServer[]>(tx(db, "servers", "readonly").getAll());
  const maxOrder = existing.reduce((max, s) => Math.max(max, s.sortOrder), -1);

  const server: StoredServer = {
    id: input.id ?? uuid(),
    name: input.name,
    ...(input.serverKind ? { serverKind: input.serverKind } : {}),
    ...(input.connectionConfig ? { connectionConfig: input.connectionConfig } : {}),
    baseUrl: input.baseUrl,
    ...(input.graphqlPath ? { graphqlPath: input.graphqlPath } : {}),
    ...(input.restPath ? { restPath: input.restPath } : {}),
    authType: input.authType as StoredServer["authType"],
    authConfig: input.authConfig ?? "{}",
    isProduction: input.isProduction ?? false,
    ...(input.timeoutMs != null ? { timeoutMs: input.timeoutMs } : {}),
    sortOrder: maxOrder + 1,
    createdAt: now(),
    updatedAt: now(),
  };

  const transaction = db.transaction("servers", "readwrite");
  const store = transaction.objectStore("servers");
  store.put(server);
  await txComplete(transaction);
  return server;
}

export async function updateServer(
  id: string,
  updates: Partial<StoredServer>,
): Promise<StoredServer> {
  const db = await getDb();
  const existing = await req<StoredServer | undefined>(
    tx(db, "servers", "readonly").get(id),
  );
  if (!existing) throw new Error(`Server ${id} not found`);

  const updated: StoredServer = {
    ...existing,
    ...updates,
    id, // never overwrite key
    updatedAt: now(),
  };

  const transaction = db.transaction("servers", "readwrite");
  const store = transaction.objectStore("servers");
  store.put(updated);
  await txComplete(transaction);
  return updated;
}

export async function setServerSharedState(
  id: string,
  state: {
    sharedConfigId?: string | undefined;
    sharedRole?: StoredServer["sharedRole"];
    sharedLastKnownVersion?: number | undefined;
    sharedCapability?: string | undefined;
  },
): Promise<StoredServer> {
  return updateServer(id, state);
}

export async function deleteServer(id: string): Promise<void> {
  const db = await getDb();

  // Delete server
  const serverTx = db.transaction("servers", "readwrite");
  const serverStore = serverTx.objectStore("servers");
  serverStore.delete(id);
  await txComplete(serverTx);

  // Delete related queries
  const queryTx = db.transaction("queries", "readwrite");
  const queryStore = queryTx.objectStore("queries");
  const queryIndex = queryStore.index("serverId");
  const queries = await req<StoredQuery[]>(queryIndex.getAll(id));
  for (const q of queries) {
    queryStore.delete(q.id);
  }
  await txComplete(queryTx);

  // Delete schema cache
  const cacheStore = tx(db, "schemaCache", "readwrite");
  await req(cacheStore.delete(id));

  // Delete history
  const historyTx = db.transaction("history", "readwrite");
  const historyStore = historyTx.objectStore("history");
  const historyIndex = historyStore.index("serverId");
  const entries = await req<StoredHistoryEntry[]>(historyIndex.getAll(id));
  for (const e of entries) {
    if (e.id != null) historyStore.delete(e.id);
  }
  await txComplete(historyTx);

  const refsTx = db.transaction("sharedConfigRefs", "readwrite");
  const refsStore = refsTx.objectStore("sharedConfigRefs");
  const refsIndex = refsStore.index("linkedServerId");
  const refs = await req<StoredSharedConfigRef[]>(refsIndex.getAll(id));
  for (const ref of refs) refsStore.delete(ref.configId);
  await txComplete(refsTx);

  // Delete server-scoped vault entries
  const vaultTx = db.transaction("vaultEntries", "readwrite");
  const vaultStore = vaultTx.objectStore("vaultEntries");
  const vaultEntries = await req<StoredVaultEntry[]>(vaultStore.getAll());
  for (const entry of vaultEntries) {
    if (entry.scopeType === "server" && entry.scopeServerId === id) {
      vaultStore.delete(entry.id);
    }
  }
  await txComplete(vaultTx);
}

// ── Queries CRUD ─────────────────────────────────────────────────────

export async function listQueries(serverId: string): Promise<StoredQuery[]> {
  const db = await getDb();
  const store = tx(db, "queries", "readonly");
  const index = store.index("serverId");
  const queries = await req<StoredQuery[]>(index.getAll(serverId));
  return queries.sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function listAllQueries(): Promise<StoredQuery[]> {
  const db = await getDb();
  const queries = await req<StoredQuery[]>(tx(db, "queries", "readonly").getAll());
  return queries.sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function createQuery(input: {
  id?: string;
  serverId: string;
  name: string;
  category?: string;
  systemPreset?: QuerySystemPreset;
  pinnedAt?: string | null;
  queryText?: string;
  variables?: string;
  headers?: string;
  remarks?: string;
  riskLevel?: QueryRiskLevel;
  requestType?: RequestType;
  operationConfig?: QueryOperationConfig;
  restMethod?: RestMethod;
  restPath?: string;
  restParams?: RestParam[];
  restContentType?: RestContentType;
}): Promise<StoredQuery> {
  const db = await getDb();

  // Determine next sortOrder for this server
  const store = tx(db, "queries", "readonly");
  const index = store.index("serverId");
  const existing = await req<StoredQuery[]>(index.getAll(input.serverId));
  const maxOrder = existing.reduce((max, q) => Math.max(max, q.sortOrder), -1);

  const normalizedRest = normalizeRestPathAndParams(input.restPath, input.restParams);
  const query: StoredQuery = {
    id: input.id ?? uuid(),
    serverId: input.serverId,
    name: input.name,
    queryText: input.queryText ?? "",
    variables: input.variables ?? "{}",
    headers: input.headers ?? "{}",
    remarks: normalizeRemarks(input.remarks),
    riskLevel: normalizeQueryRiskLevel(input.riskLevel ?? DEFAULT_QUERY_RISK_LEVEL),
    sortOrder: maxOrder + 1,
    createdAt: now(),
    updatedAt: now(),
    ...(input.category ? { category: input.category } : {}),
    ...(input.systemPreset ? { systemPreset: input.systemPreset } : {}),
    ...(input.pinnedAt !== undefined ? { pinnedAt: input.pinnedAt } : {}),
    ...(input.requestType ? { requestType: input.requestType } : {}),
    ...(input.operationConfig ? { operationConfig: input.operationConfig } : {}),
    ...(input.restMethod ? { restMethod: input.restMethod } : {}),
    ...(normalizedRest.restPath != null ? { restPath: normalizedRest.restPath } : {}),
    ...(normalizedRest.restParams.length > 0 ? { restParams: normalizedRest.restParams } : { restParams: [] }),
    ...(input.restContentType ? { restContentType: input.restContentType } : {}),
  };

  const transaction = db.transaction("queries", "readwrite");
  const writeStore = transaction.objectStore("queries");
  writeStore.put(query);
  await txComplete(transaction);
  return query;
}

export async function updateQuery(
  id: string,
  updates: Partial<StoredQuery>,
): Promise<StoredQuery> {
  const db = await getDb();
  const existing = await req<StoredQuery | undefined>(
    tx(db, "queries", "readonly").get(id),
  );
  if (!existing) throw new Error(`Query ${id} not found`);

  const normalizedRest = normalizeRestPathAndParams(
    updates.restPath ?? existing.restPath,
    updates.restParams ?? existing.restParams,
  );
  const updated: StoredQuery = {
    ...existing,
    ...updates,
    id, // never overwrite key
    remarks: normalizeRemarks(updates.remarks ?? existing.remarks),
    riskLevel: normalizeQueryRiskLevel(updates.riskLevel ?? existing.riskLevel),
    restPath: normalizedRest.restPath,
    restParams: normalizedRest.restParams,
    updatedAt: now(),
  };

  const transaction = db.transaction("queries", "readwrite");
  const store = transaction.objectStore("queries");
  store.put(updated);
  await txComplete(transaction);
  return updated;
}

export async function deleteQuery(id: string): Promise<void> {
  const db = await getDb();
  await req(tx(db, "queries", "readwrite").delete(id));
}

export async function reorderQueries(
  serverId: string,
  queryIds: string[],
): Promise<void> {
  const db = await getDb();
  const existingQueries = await listQueries(serverId);
  const byId = new Map(existingQueries.map((query) => [query.id, query]));
  const transaction = db.transaction("queries", "readwrite");
  const store = transaction.objectStore("queries");

  for (let i = 0; i < queryIds.length; i++) {
    const existing = byId.get(queryIds[i]!);
    if (existing && existing.serverId === serverId) {
      store.put({
        ...existing,
        sortOrder: i,
        updatedAt: now(),
      });
    }
  }

  await txComplete(transaction);
}

// ── Schema Cache ─────────────────────────────────────────────────────

export async function getSchemaCache(
  serverId: string,
): Promise<StoredSchemaCache | null> {
  const db = await getDb();
  const result = await req<StoredSchemaCache | undefined>(
    tx(db, "schemaCache", "readonly").get(serverId),
  );
  return result ?? null;
}

export async function setSchemaCache(
  serverId: string,
  introspectionJson: string,
  options?: {
    sdl?: string;
    catalogKind?: CatalogKind;
    catalogJson?: string;
  },
): Promise<StoredSchemaCache> {
  const db = await getDb();
  const entry: StoredSchemaCache = {
    serverId,
    introspectionJson,
    ...(options?.sdl !== undefined ? { sdl: options.sdl } : {}),
    ...(options?.catalogKind !== undefined ? { catalogKind: options.catalogKind } : {}),
    ...(options?.catalogJson !== undefined ? { catalogJson: options.catalogJson } : {}),
    fetchedAt: now(),
  };
  await req(tx(db, "schemaCache", "readwrite").put(entry));
  return entry;
}

// ── History ──────────────────────────────────────────────────────────

export async function listHistory(
  serverId: string,
  limit = 100,
): Promise<StoredHistoryEntry[]> {
  const db = await getDb();
  const store = tx(db, "history", "readonly");
  const index = store.index("serverId");
  const entries = await req<StoredHistoryEntry[]>(index.getAll(serverId));

  // Sort descending by executedAt, then limit
  entries.sort(
    (a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime(),
  );
  return entries.slice(0, limit);
}

export async function addHistoryEntry(
  entry: Omit<StoredHistoryEntry, "id">,
): Promise<StoredHistoryEntry> {
  const db = await getDb();
  const store = tx(db, "history", "readwrite");
  const id = await req<IDBValidKey>(store.add(entry));
  return { ...entry, id: id as number };
}

export async function clearHistory(serverId: string): Promise<void> {
  const db = await getDb();
  const transaction = db.transaction("history", "readwrite");
  const store = transaction.objectStore("history");
  const index = store.index("serverId");
  const entries = await req<StoredHistoryEntry[]>(index.getAll(serverId));
  for (const e of entries) {
    if (e.id != null) store.delete(e.id);
  }
  await txComplete(transaction);
}

// ── Secret Registry ────────────────────────────────────────────────────

export async function listSecrets(): Promise<StoredSecret[]> {
  const db = await getDb();
  const secrets = await req<StoredSecret[]>(tx(db, "secretRegistry", "readonly").getAll());
  return secrets.sort((a, b) => a.name.localeCompare(b.name));
}

export async function upsertSecret(name: string, value: string): Promise<StoredSecret> {
  const db = await getDb();
  const store = tx(db, "secretRegistry", "readwrite");
  const existing = await req<StoredSecret | undefined>(store.get(name));
  const nowIso = now();
  const secret: StoredSecret = {
    name,
    value,
    createdAt: existing?.createdAt ?? nowIso,
    updatedAt: nowIso,
  };
  await req(store.put(secret));
  return secret;
}

export async function deleteSecret(name: string): Promise<void> {
  const db = await getDb();
  await req(tx(db, "secretRegistry", "readwrite").delete(name));
}

export async function exportSecrets(): Promise<SecretExportData> {
  return {
    version: 1,
    exportedAt: now(),
    secrets: await listSecrets(),
  };
}

export async function importSecrets(data: SecretExportData): Promise<void> {
  if (data.version !== 1) throw new Error(`Unsupported secret export version: ${data.version}`);
  const db = await getDb();
  const transaction = db.transaction("secretRegistry", "readwrite");
  const store = transaction.objectStore("secretRegistry");
  for (const secret of data.secrets) store.put(secret);
  await txComplete(transaction);
}

// ── Shared Config Refs ────────────────────────────────────────────────

export async function listSharedConfigRefs(): Promise<StoredSharedConfigRef[]> {
  const db = await getDb();
  return req<StoredSharedConfigRef[]>(tx(db, "sharedConfigRefs", "readonly").getAll());
}

export async function getSharedConfigRef(configId: string): Promise<StoredSharedConfigRef | undefined> {
  const db = await getDb();
  return req<StoredSharedConfigRef | undefined>(tx(db, "sharedConfigRefs", "readonly").get(configId));
}

export async function getSharedConfigRefByServerId(
  linkedServerId: string,
): Promise<StoredSharedConfigRef | undefined> {
  const db = await getDb();
  const store = tx(db, "sharedConfigRefs", "readonly");
  const refs = await req<StoredSharedConfigRef[]>(store.index("linkedServerId").getAll(linkedServerId));
  return refs[0];
}

export async function putSharedConfigRef(
  input: Omit<StoredSharedConfigRef, "createdAt" | "updatedAt">,
): Promise<StoredSharedConfigRef> {
  const db = await getDb();
  const store = tx(db, "sharedConfigRefs", "readwrite");
  const existing = await req<StoredSharedConfigRef | undefined>(store.get(input.configId));
  const ref: StoredSharedConfigRef = {
    ...input,
    createdAt: existing?.createdAt ?? now(),
    updatedAt: now(),
  };
  await req(store.put(ref));
  return ref;
}

export async function deleteSharedConfigRef(configId: string): Promise<void> {
  const db = await getDb();
  await req(tx(db, "sharedConfigRefs", "readwrite").delete(configId));
}

export async function replaceServerBundle(input: {
  mode: "create" | "update";
  server: StoredServer;
  queries: StoredQuery[];
  sharedRef?: StoredSharedConfigRef;
}): Promise<void> {
  const db = await getDb();
  const transaction = db.transaction(["servers", "queries", "sharedConfigRefs"], "readwrite");

  const serverStore = transaction.objectStore("servers");
  const queryStore = transaction.objectStore("queries");
  const queryIndex = queryStore.index("serverId");
  const refStore = transaction.objectStore("sharedConfigRefs");
  const refIndex = refStore.index("linkedServerId");

  if (input.mode === "create") {
    serverStore.add(input.server);
  } else {
    serverStore.put(input.server);
  }

  const existingQueries = await req<StoredQuery[]>(queryIndex.getAll(input.server.id));
  const localOnlyQueries = existingQueries.filter((query) => query.systemPreset);

  for (const query of existingQueries) {
    if (query.systemPreset) continue;
    queryStore.delete(query.id);
  }
  for (const query of input.queries) {
    queryStore.put(query);
  }
  for (const query of localOnlyQueries) {
    queryStore.put(query);
  }

  const existingRefs = await req<StoredSharedConfigRef[]>(refIndex.getAll(input.server.id));
  for (const ref of existingRefs) {
    if (!input.sharedRef || ref.configId !== input.sharedRef.configId) {
      refStore.delete(ref.configId);
    }
  }
  if (input.sharedRef) {
    refStore.put(input.sharedRef);
  }

  await txComplete(transaction);
}

export async function listHistoryForQuery(
  serverId: string,
  queryId: string,
  limit = 100,
): Promise<StoredHistoryEntry[]> {
  const db = await getDb();
  const store = tx(db, "history", "readonly");
  const index = store.index("serverId");
  const entries = await req<StoredHistoryEntry[]>(index.getAll(serverId));

  const filtered = entries.filter((e) => e.queryId === queryId);
  filtered.sort(
    (a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime(),
  );
  return filtered.slice(0, limit);
}

// ── Vault ────────────────────────────────────────────────────────────

export async function getVaultMeta(): Promise<StoredVaultMeta | null> {
  const db = await getDb();
  const meta = await req<StoredVaultMeta | undefined>(
    tx(db, "vaultMeta", "readonly").get("vault"),
  );
  return meta ?? null;
}

export async function setVaultMeta(meta: StoredVaultMeta): Promise<StoredVaultMeta> {
  const db = await getDb();
  await req(tx(db, "vaultMeta", "readwrite").put(meta));
  return meta;
}

export async function clearVaultMeta(): Promise<void> {
  const db = await getDb();
  await req(tx(db, "vaultMeta", "readwrite").delete("vault"));
}

export async function listVaultEntries(): Promise<StoredVaultEntry[]> {
  const db = await getDb();
  const entries = await req<StoredVaultEntry[]>(tx(db, "vaultEntries", "readonly").getAll());
  return entries.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
    if (a.key !== b.key) return a.key.localeCompare(b.key);
    return a.scopeType.localeCompare(b.scopeType);
  });
}

export async function getVaultEntry(id: string): Promise<StoredVaultEntry | null> {
  const db = await getDb();
  const entry = await req<StoredVaultEntry | undefined>(
    tx(db, "vaultEntries", "readonly").get(id),
  );
  return entry ?? null;
}

export async function createVaultEntry(input: {
  key: string;
  kind: VaultEntryKind;
  scopeType: VaultScopeType;
  scopeServerId?: string;
  description?: string;
  ciphertext: string;
  iv: string;
}): Promise<StoredVaultEntry> {
  const db = await getDb();
  const existing = await listVaultEntries();
  const duplicate = existing.find((entry) =>
    entry.key === input.key
    && entry.kind === input.kind
    && entry.scopeType === input.scopeType
    && (entry.scopeServerId ?? "") === (input.scopeServerId ?? ""),
  );
  if (duplicate) {
    throw new Error(`Vault entry already exists: ${input.kind}:${input.key}`);
  }

  const entry: StoredVaultEntry = {
    id: uuid(),
    key: input.key,
    kind: input.kind,
    scopeType: input.scopeType,
    ...(input.scopeServerId ? { scopeServerId: input.scopeServerId } : {}),
    description: input.description ?? "",
    ciphertext: input.ciphertext,
    iv: input.iv,
    createdAt: now(),
    updatedAt: now(),
  };

  await req(tx(db, "vaultEntries", "readwrite").put(entry));
  return entry;
}

export async function updateVaultEntry(
  id: string,
  updates: Partial<StoredVaultEntry>,
): Promise<StoredVaultEntry> {
  const db = await getDb();
  const existing = await req<StoredVaultEntry | undefined>(
    tx(db, "vaultEntries", "readonly").get(id),
  );
  if (!existing) throw new Error(`Vault entry ${id} not found`);

  const updated: StoredVaultEntry = {
    ...existing,
    ...updates,
    id,
    updatedAt: now(),
  };

  const allEntries = await listVaultEntries();
  const duplicate = allEntries.find((entry) =>
    entry.id !== id
    && entry.key === updated.key
    && entry.kind === updated.kind
    && entry.scopeType === updated.scopeType
    && (entry.scopeServerId ?? "") === (updated.scopeServerId ?? ""),
  );
  if (duplicate) {
    throw new Error(`Vault entry already exists: ${updated.kind}:${updated.key}`);
  }

  await req(tx(db, "vaultEntries", "readwrite").put(updated));
  return updated;
}

export async function deleteVaultEntry(id: string): Promise<void> {
  const db = await getDb();
  await req(tx(db, "vaultEntries", "readwrite").delete(id));
}

export async function getRememberedVaultKey(): Promise<CryptoKey | null> {
  const db = await getDb();
  const record = await req<StoredVaultRememberedKey | undefined>(
    tx(db, "vaultDeviceKeys", "readonly").get("remembered"),
  );
  return record?.key ?? null;
}

export async function hasRememberedVaultKey(): Promise<boolean> {
  return (await getRememberedVaultKey()) !== null;
}

export async function setRememberedVaultKey(key: CryptoKey): Promise<void> {
  const db = await getDb();
  const existing = await req<StoredVaultRememberedKey | undefined>(
    tx(db, "vaultDeviceKeys", "readonly").get("remembered"),
  );
  const timestamp = now();
  const record: StoredVaultRememberedKey = {
    id: "remembered",
    key,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
  const transaction = db.transaction("vaultDeviceKeys", "readwrite");
  transaction.objectStore("vaultDeviceKeys").put(record);
  await txComplete(transaction);
}

export async function clearRememberedVaultKey(): Promise<void> {
  const db = await getDb();
  const transaction = db.transaction("vaultDeviceKeys", "readwrite");
  transaction.objectStore("vaultDeviceKeys").delete("remembered");
  await txComplete(transaction);
}

export async function replaceVaultData(
  meta: StoredVaultMeta,
  entries: StoredVaultEntry[],
): Promise<void> {
  const db = await getDb();

  const metaTx = db.transaction("vaultMeta", "readwrite");
  const metaStore = metaTx.objectStore("vaultMeta");
  metaStore.clear();
  metaStore.put(meta);
  await txComplete(metaTx);

  const entryTx = db.transaction("vaultEntries", "readwrite");
  const entryStore = entryTx.objectStore("vaultEntries");
  entryStore.clear();
  for (const entry of entries) entryStore.put(entry);
  await txComplete(entryTx);
}

// ── Export / Import ──────────────────────────────────────────────────

export async function exportServerData(serverId: string): Promise<SpuerhundExport> {
  const db = await getDb();

  const server = await req<StoredServer | undefined>(
    tx(db, "servers", "readonly").get(serverId),
  );
  if (!server) throw new Error(`Server ${serverId} not found`);

  const queryStore = tx(db, "queries", "readonly");
  const queryIndex = queryStore.index("serverId");
  const queries = await req<StoredQuery[]>(queryIndex.getAll(serverId));

  const schemaEntry = await req<StoredSchemaCache | undefined>(
    tx(db, "schemaCache", "readonly").get(serverId),
  );

  const historyStore = tx(db, "history", "readonly");
  const historyIndex = historyStore.index("serverId");
  const history = await req<StoredHistoryEntry[]>(historyIndex.getAll(serverId));

  return {
    version: 2,
    exportedAt: now(),
    servers: [server],
    queries,
    schemaCache: schemaEntry ? [schemaEntry] : [],
    history,
  };
}

export async function exportAllData(): Promise<SpuerhundExport> {
  const db = await getDb();

  const servers = await req<StoredServer[]>(tx(db, "servers", "readonly").getAll());
  const queries = await req<StoredQuery[]>(tx(db, "queries", "readonly").getAll());
  const schemaCache = await req<StoredSchemaCache[]>(
    tx(db, "schemaCache", "readonly").getAll(),
  );
  const history = await req<StoredHistoryEntry[]>(
    tx(db, "history", "readonly").getAll(),
  );

  return {
    version: 2,
    exportedAt: now(),
    servers,
    queries,
    schemaCache,
    history,
  };
}

export async function exportVaultData(): Promise<SpuerhundVaultExport> {
  const meta = await getVaultMeta();
  if (!meta) throw new Error("Vault not initialized");

  return {
    version: 1,
    exportedAt: now(),
    meta,
    entries: await listVaultEntries(),
  };
}

export async function importVaultData(data: SpuerhundVaultExport): Promise<void> {
  if (data.version !== 1) {
    throw new Error(`Unsupported vault export version: ${data.version}`);
  }

  await replaceVaultData(data.meta, data.entries);
}

export async function importData(data: SpuerhundExport | LegacySpuerhundExport): Promise<void> {
  const version = data.version;
  if (version !== 1 && version !== 2) {
    throw new Error(`Unsupported export version: ${version}`);
  }

  const db = await getDb();
  const existingServers = await listServers();
  const existingQueries = await req<StoredQuery[]>(tx(db, "queries", "readonly").getAll());

  const mergePlan = buildImportMergePlan({
    existingServers,
    existingQueries,
    importedData: data,
    now: now(),
    makeId: uuid,
  });

  const serverTx = db.transaction("servers", "readwrite");
  const serverStore = serverTx.objectStore("servers");
  for (const server of mergePlan.serversToUpsert) {
    serverStore.put(server);
  }
  await txComplete(serverTx);

  const queryTx = db.transaction("queries", "readwrite");
  const queryStore = queryTx.objectStore("queries");
  const queryIndex = queryStore.index("serverId");
  for (const serverId of mergePlan.replacedServerIds) {
    const existingForServer = await req<StoredQuery[]>(queryIndex.getAll(serverId));
    for (const query of existingForServer) {
      queryStore.delete(query.id);
    }
  }
  for (const query of mergePlan.queriesToInsert) {
    queryStore.put(query);
  }
  await txComplete(queryTx);

  const cacheTx = db.transaction("schemaCache", "readwrite");
  const cacheStore = cacheTx.objectStore("schemaCache");
  for (const serverId of mergePlan.replacedServerIds) {
    cacheStore.delete(serverId);
  }
  for (const cacheEntry of mergePlan.schemaCacheToUpsert) {
    cacheStore.put(cacheEntry);
  }
  await txComplete(cacheTx);

  const historyTx = db.transaction("history", "readwrite");
  const historyStore = historyTx.objectStore("history");
  const historyIndex = historyStore.index("serverId");
  for (const serverId of mergePlan.replacedServerIds) {
    const existingForServer = await req<StoredHistoryEntry[]>(historyIndex.getAll(serverId));
    for (const entry of existingForServer) {
      if (entry.id != null) historyStore.delete(entry.id);
    }
  }
  for (const entry of mergePlan.historyToInsert) {
    historyStore.put(entry);
  }
  await txComplete(historyTx);
}

// Re-open DB when context switches (anonymous ↔ signed-in)
onDbSwitch(async () => {
  await reopenDb();
});
